import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { useAuthContext } from '@/context/AuthContext';
import ENDPOINTS from '@/api';

export type SocialTaskType = 'twitter' | 'telegram' | 'email';

export interface SocialTaskStatus {
  twitter: boolean;
  telegram: boolean;
  email: boolean;
}

export interface CompleteSocialTaskResponse {
  pointsAwarded: number;
  newBalance: number;
  taskType: SocialTaskType;
}

/**
 * Hook to fetch social task completion status
 */
export function useSocialTaskStatus() {
  const { isAuthenticated } = useAuthContext();

  return useQuery({
    queryKey: ['socialTaskStatus'],
    queryFn: async (): Promise<SocialTaskStatus> => {
      const response = await fetch(ENDPOINTS.socialTaskStatus, {
        credentials: 'include',
      });

      const json = await response.json();

      if (!response.ok || json.code !== 0) {
        throw new Error(json.message || 'Failed to fetch social task status');
      }

      return json.data;
    },
    enabled: isAuthenticated,
    staleTime: 30 * 1000, // 30 seconds
    refetchOnWindowFocus: true,
  });
}

/**
 * Hook to complete a social task and claim points
 */
export function useCompleteSocialTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      taskType: SocialTaskType
    ): Promise<CompleteSocialTaskResponse> => {
      const response = await fetch(ENDPOINTS.socialTaskComplete, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ task_type: taskType }),
      });

      const json = await response.json();

      if (!response.ok || json.code !== 0) {
        throw new Error(
          json.message || 'Failed to complete social task'
        );
      }

      return json.data;
    },
    onSuccess: (data) => {
      toast.success(
        `Success! You earned ${data.pointsAwarded.toLocaleString()} points!`
      );

      // Invalidate related queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['socialTaskStatus'] });
      queryClient.invalidateQueries({ queryKey: ['userPoints'] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to complete task. Please try again.');
    },
  });
}
